import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Axios} from 'services/axios';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import moment from 'moment';
import MDBadge from 'components/MDBadge';
import MDBox from 'components/MDBox';
import {useForm, Controller} from 'react-hook-form';
import {Confirm, Loading, Notify} from 'notiflix';
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  FormControlLabel,
  Switch,
  Typography,
  Box,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Name', accessor: 'name', width: '25%', align: 'left'},
  {
    Header: 'Description',
    accessor: 'description',
    width: '30%',
    align: 'left',
  },
  {Header: 'image', accessor: 'image_url', align: 'center', width: '5%'},
  {Header: 'Created at', accessor: 'createdOn', align: 'center'},
  {Header: 'Updated at', accessor: 'updatedOn', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'center'},
];

const getRow = (
  product,
  idx,
  handleGotoDetails,
  handleDeleteProduct,
  handleUpdateProduct,
  canUpdate,
  canDelete,
) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography variant="button" fontWeight="medium">
      {product.name}
    </MDTypography>
  ),
  description: (
    <MDTypography variant="caption">{product.description}</MDTypography>
  ),
  image_url: (
    <Box
      component="img"
      sx={{
        height: 50,
        width: 'auto',
      }}
      alt="No image found"
      src={product.image_url || product.image}
    />
  ),
  createdOn: (
    <MDTypography variant="caption">
      {moment(product.createdAt).calendar()}
    </MDTypography>
  ),
  updatedOn: (
    <MDTypography variant="caption">
      {moment(product.updatedAt).calendar()}
    </MDTypography>
  ),
  action: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent="Packages"
        color="info"
        variant="gradient"
        size="sm"
        onClick={() => handleGotoDetails(product.id, product?.category?.id)}
        style={{cursor: 'pointer'}}
      />

      {canUpdate && (
        <MDBadge
          badgeContent="Update"
          color="success"
          variant="gradient"
          size="sm"
          onClick={() => handleUpdateProduct(product)}
          style={{cursor: 'pointer'}}
        />
      )}

      {canDelete && (
        <MDBadge
          badgeContent="Delete"
          color="error"
          variant="gradient"
          size="sm"
          onClick={() => handleDeleteProduct(product.id)}
          style={{cursor: 'pointer'}}
        />
      )}
    </MDBox>
  ),
});

// Badge values are stored as-is and rendered as the tag text on the storefront.
// "Highest ROI" marks the featured (middle) card in the category "Big Three" section.
const BADGE_OPTIONS = [
  {value: 'BEST SELLER', label: 'Best Seller'},
  {value: 'Most Bought This Month', label: 'Most Bought This Month'},
  {value: 'Highest ROI', label: 'Highest ROI (Featured)'},
  {value: 'Most Underrated', label: 'Most Underrated'},
];

const Products = () => {
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');
  // Fetch all categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [active, setActive] = useState(true);

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    control,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: selectedProduct ? selectedProduct.name : '',
      description: selectedProduct ? selectedProduct.description : '',
      image_url: selectedProduct ? selectedProduct.image_url : '',
      image: null,
      active: selectedProduct ? selectedProduct.active : true,
      url: null,
      badge: selectedProduct ? selectedProduct.badge : '',
      badgeColor: selectedProduct?.badgeColor || '#eff4ff',
      badgeTextColor: selectedProduct?.badgeTextColor || '#1d5dff',
      comboBg: selectedProduct?.comboBg || '#dbeafe',
      productType: selectedProduct?.productType || 'single',
    },
  });

  // Combo products get a free-text badge (e.g. "Dominate", "Power") instead
  // of the fixed BADGE_OPTIONS list used by single products.
  const isComboProduct = watch('productType') === 'combo';

  const [selectedCatObj, setSelectedCatObj] = useState(
    categories.find(cat => cat.id === selectedCategory),
  );

  const handleGotoDetails = useCallback(
    (productId, categoryId) => {
      if (selectedCategory.length) {
        navigate(
          `/products/package/${productId}?categoryId=${selectedCategory}`,
        );
      } else {
        navigate(`/products/package/${productId}?categoryId=${categoryId}`);
      }
    },
    [navigate, selectedCategory],
  );

  const handleDeleteProduct = useCallback(productID => {
    // Show the confirmation dialog
    Confirm.show(
      'Confirm Deletion',
      'Are you sure you want to delete this product?',
      'Yes',
      'No',
      function () {
        // User clicked "Yes"
        Loading.circle('Deleting Product');
        const product = products.find(p => p.id === productID);
        Axios.delete(`/shop/products/${productID}/delete-product?label=${encodeURIComponent(product?.name || '')}`)
          .then(res => {
            Notify.success(res.data.message || 'Product deleted successfully');
            // Remove the deleted product from the state
            setProducts(prev =>
              prev.filter(product => product.id !== productID),
            );
          })
          .catch(err => {
            Notify.failure(
              err.response.data.message || 'Failed to delete product',
            );
          })
          .finally(() => {
            Loading.remove('Deleting Product');
          });
      },
      function () {
        // if clicked "No" or closed the dialog
      },
      {
        width: '320px',
        borderRadius: '8px',
        backgroundColor: '#fff',
      },
    );
  }, [products]);

  const fetchAllProducts = async () => {
    Loading.circle('Fetching Products');
    setProducts([]);
    try {
      const response = await Axios.fetch('/shop/getAllProducts');
      setProducts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching all products:', err);
    } finally {
      Loading.remove('Fetching Products');
    }
  };

  const onSubmit = async data => {
    Loading.circle('Adding / Updating Product');
    data = data ?? selectedProduct;
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description);
    formData.append('active', data.active);
    formData.append('categoryId', selectedCategory ?? data.category.id);
    formData.append('url', data.url);
    formData.append('slug', data.slug);
    formData.append('badge', data.badge || '');
    formData.append('badgeColor', data.badgeColor || '');
    formData.append('badgeTextColor', data.badgeTextColor || '');
    formData.append('comboBg', data.comboBg || '');
    formData.append('productType', data.productType || 'single');

    if (data.image?.[0]) {
      formData.append('image', data.image[0]);
    } else if (data.image_url) {
      formData.append('image_url', data.image_url);
    }
    const url = selectedProduct
      ? `/shop/addUpdateProduct?productId=${selectedProduct.id}`
      : '/shop/addUpdateProduct';
    try {
      const response = await Axios.post(url, formData, {
        'Content-type': 'multipart/form-data',
      });

      // console.log('Product added/updated:', response.data);
      setProducts([
        ...products.filter(x => x.id !== response.data.data.id),
        response.data.data,
      ]);

      Notify.success(response.data.message || 'Feature updated successfully');
    } catch (error) {
      Notify.failure(
        error.response.data.message || 'Failed to add/update product',
      );
    } finally {
      setOpenDialog(false);
      Loading.remove('Adding / Updating Product');
    }
  };

  const handleUpdateProduct = product => {
    setOpenDialog(true);
    Object.keys(product).forEach(key => {
      setValue(key, product[key]);
    });
    setSelectedProduct(product);
    const cat = product.category || categories.find(c => c.id === product.categoryId);
    setSelectedCatObj(cat);
    setSelectedCategory([cat?.id || product.categoryId]);
  };

  // Stable wrapper so the table rows below don't change on every render
  const updateProductRef = useRef(handleUpdateProduct);
  updateProductRef.current = handleUpdateProduct;
  const onUpdateProduct = useCallback(product => updateProductRef.current(product), []);

  // Rows are memoised: the table goes back to page 1 whenever its data changes,
  // so rebuilding them on every render sent the list back to page 1 after
  // moving to page 2. They now change only with the products / search.
  const filteredProducts = useMemo(
    () =>
      products.filter(
        p => !search || p.name?.toLowerCase().includes(search.toLowerCase()),
      ),
    [products, search],
  );
  const tableData = useMemo(
    () => ({
      columns,
      rows: filteredProducts.map((row, idx) =>
        getRow(row, idx, handleGotoDetails, handleDeleteProduct, onUpdateProduct),
      ),
    }),
    [filteredProducts, handleGotoDetails, handleDeleteProduct, onUpdateProduct],
  );

  const handleAddNew = () => {
    setSelectedProduct(null);
    reset();
    setOpenDialog(true);
  };

  const handleChange = event => {
    // if (event.target.value.trim()) {
    setSelectedCategory(event.target.value);
    // }
  };

  const fetchCategories = async () => {
    try {
      const response = await Axios.fetch('/shop/getAllCategories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch all products by category
  const fetchProductsByCategory = async () => {
    if (!selectedCategory || !selectedCategory.length) {
      fetchAllProducts();
      setSelectedCatObj(null);
      return;
    }
    Loading.circle('Fetching Products');
    setSelectedCatObj(categories.find(cat => cat.id === selectedCategory));
    setProducts([]);
    try {
      const response = await Axios.fetch(
        `/shop/getProductsByCategory/${selectedCategory}`,
      );
      setProducts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching products by category:', err);
    } finally {
      Loading.remove('Fetching Products');
    }
  };

  // Call fetch on component mount
  useEffect(() => {
    if (!canRead) return;
    fetchCategories();
    fetchAllProducts();
  }, [canRead]);

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* <div className="productsPage">Products</div> */}

      <div>
        <MDBox
          className="topSection"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            mt: 2,
            mb: 1,
          }}>
          {/* Left: search */}
          <TextField
            placeholder="Search by product name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                background: '#f8fafc',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
              },
            }}
            InputProps={{ startAdornment: <span style={{ marginRight: 6, color: '#94a3b8' }}>🔍</span> }}
          />
          {/* Right: category filter */}
          <MDBox display="flex" alignItems="center" gap="10px">
            <FormControl sx={{width: 300}}>
              <InputLabel id="demo-multiple-name-label">Select Category</InputLabel>
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                sx={{height: 45}}
                value={selectedCategory.id}
                onChange={handleChange}
                input={<OutlinedInput label="Category Name" />}>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MDButton
              sx={{width: 100, height: 45}}
              onClick={fetchProductsByCategory}
              variant="contained"
              color="info">
              Filter
            </MDButton>
          </MDBox>
        </MDBox>

        <MDBox mt={4}>
          <MDBox
            mb={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <h4 style={{textAlign: 'center'}}>
              {selectedCatObj ? selectedCatObj.name : 'All Products'}
            </h4>

            {selectedCatObj && canCreate && (
              <MDButton variant="contained" color="info" onClick={handleAddNew}>
                Add New Product to Category
              </MDButton>
            )}
          </MDBox>
          {filteredProducts.length > 0 && (
            <DataTable
              table={{
                columns,
                rows: products
                  .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()))
                  .map((row, idx) =>
                    getRow(row, idx, handleGotoDetails, handleDeleteProduct, handleUpdateProduct, canUpdate, canDelete),
                  ),
              }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
              hoverEffect
            />
          )}
          {selectedCatObj && !products.length && (
            <Typography variant="h6">
              No Data Found, Please add product to category
            </Typography>
          )}
        </MDBox>
      </div>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {selectedProduct
            ? `Edit Product In Category : ${selectedCatObj?.name}`
            : `Create Product In Category : ${selectedCatObj?.name}`}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* NAME */}
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              {...register('name', {required: 'Name is required'})}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            {/* DESCRIPTION */}
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              {...register('description', {
                required: 'Description is required',
              })}
              error={!!errors.description}
              helperText={errors.description?.message}
            />

            {/* SLUG (required) */}
            <TextField
              label="Slug *"
              fullWidth
              margin="normal"
              placeholder="e.g. web-design-pro"
              {...register('slug', { required: 'Slug is required' })}
              error={!!errors.slug}
              helperText={errors.slug?.message || 'Used in URLs — lowercase, hyphens only.'}
            />

            {/* URL (optional) */}
            <TextField
              label="URL"
              fullWidth
              margin="normal"
              {...register('url')}
            />

            {/* BADGE — free text for combo products, enum (BADGE_OPTIONS) for single */}
            {selectedProduct && (
              <MDTypography variant="caption" color="text" mt={2} display="block">
                Current Badge:{' '}
                <strong>
                  {BADGE_OPTIONS.find(o => o.value === selectedProduct.badge)?.label ||
                    selectedProduct.badge ||
                    'None'}
                </strong>
              </MDTypography>
            )}
            <Controller
              name="badge"
              control={control}
              defaultValue=""
              render={({field}) =>
                isComboProduct ? (
                  <TextField
                    label="Badge"
                    fullWidth
                    margin="normal"
                    placeholder="e.g. Dominate, Power, Global"
                    helperText="Shown as the tag on the combo card — leave empty for no badge"
                    inputProps={{maxLength: 255}}
                    {...field}
                    value={field.value || ''}
                  />
                ) : (
                  <TextField
                    select
                    label="Badge"
                    fullWidth
                    margin="normal"
                    helperText="Shown as a small tag on the product card"
                    {...field}
                    value={
                      BADGE_OPTIONS.some(o => o.value === field.value) ? field.value : ''
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {BADGE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )
              }
            />

            {/* BADGE COLORS (optional overrides for the badge background/text) */}
            <MDBox display="flex" alignItems="center" gap={3} mt={2} mb={1}>
              <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <MDTypography variant="caption">Badge Background</MDTypography>
                <Controller
                  name="badgeColor"
                  control={control}
                  defaultValue="#eff4ff"
                  render={({field}) => (
                    <input
                      type="color"
                      value={field.value || '#eff4ff'}
                      onChange={e => field.onChange(e.target.value)}
                      style={{
                        width: 40,
                        height: 40,
                        padding: 0,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                />
              </MDBox>

              <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <MDTypography variant="caption">Badge Text</MDTypography>
                <Controller
                  name="badgeTextColor"
                  control={control}
                  defaultValue="#1d5dff"
                  render={({field}) => (
                    <input
                      type="color"
                      value={field.value || '#1d5dff'}
                      onChange={e => field.onChange(e.target.value)}
                      style={{
                        width: 40,
                        height: 40,
                        padding: 0,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                />
              </MDBox>

              <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <MDTypography variant="caption">Combo Panel Background</MDTypography>
                <Controller
                  name="comboBg"
                  control={control}
                  defaultValue="#dbeafe"
                  render={({field}) => (
                    <input
                      type="color"
                      value={field.value || '#dbeafe'}
                      onChange={e => field.onChange(e.target.value)}
                      style={{
                        width: 40,
                        height: 40,
                        padding: 0,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                />
              </MDBox>
            </MDBox>

            {/* PRODUCT TYPE — 'combo' products are surfaced in the combos section on shop category pages instead of the regular product grid. */}
            <Controller
              name="productType"
              control={control}
              defaultValue="single"
              render={({field}) => (
                <TextField
                  select
                  label="Product Type"
                  fullWidth
                  margin="normal"
                  helperText="Combo products show up in the 'Stacked' combos section, not the regular product grid"
                  {...field}
                  value={field.value || 'single'}
                >
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="combo">Combo</MenuItem>
                </TextField>
              )}
            />

            {/* IMAGE URL (optional) */}
            <TextField
              label="Image URL"
              fullWidth
              margin="normal"
              {...register('image_url')}
            />

            {/* IMAGE FILE (optional) */}
            <Input
              type="file"
              fullWidth
              margin="normal"
              // For MUI <Input>, "margin" won't affect as it does for TextField
              {...register('image')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={e => setActive(e.target.checked)}
                  name="active"
                  color="primary"
                />
              }
              label="Active"
              sx={{mt: 2}}
            />

            <DialogActions>
              <MDButton
                onClick={() => setOpenDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedProduct ? 'Update product' : 'Add product'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Products;
